import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '@/core/services/auth.service';
import { Observable } from 'rxjs';
import { RoleManager } from '@/core/constants/role-config';

/**
 * RoleGuard - Protects routes based on user role
 * Verifica que el usuario tenga un rol permitido para acceder a la aplicación
 * Bloquea explícitamente roles como 'customer' y 'prospect'
 */
@Injectable({
    providedIn: 'root'
})
export class RoleGuard implements CanActivate, CanActivateChild {
    constructor(
        private authService: AuthService,
        private router: Router
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        return new Observable<boolean>(observer => {
            this.authService.isAuthenticated().then(isAuthenticated => {
                if (!isAuthenticated) {
                    console.warn('RoleGuard: Usuario no autenticado');
                    this.router.navigate(['/landing/login']);
                    observer.next(false);
                    observer.complete();
                    return;
                }

                // Obtener los roles del usuario
                this.authService.getUserRoles().then(userRoles => {
                    const normalizedRoles = userRoles.map((role) => role.toLowerCase());

                    // Verificar si al menos un rol está bloqueado
                    if (RoleManager.isBlockedRole(normalizedRoles)) {
                        console.warn(`RoleGuard: Roles '${normalizedRoles.join(', ')}' incluyen un rol bloqueado`);
                        this.router.navigate(['/landing/access']);
                        observer.next(false);
                        observer.complete();
                        return;
                    }

                    // Verificar si al menos un rol puede acceder a la aplicación
                    if (!RoleManager.canAccessApplication(normalizedRoles)) {
                        console.warn(`RoleGuard: Roles '${normalizedRoles.join(', ')}' no tienen permiso para acceder a la aplicación`);
                        this.router.navigate(['/landing/access']);
                        observer.next(false);
                        observer.complete();
                        return;
                    }

                    // Verificar si hay restricción específica de rol en la ruta
                    const requiredRoles = route.data['roles'] as string[] | undefined;
                    if (requiredRoles && requiredRoles.length > 0) {
                        const requiredRolesNormalized = requiredRoles.map((r) => r.toLowerCase());
                        const hasRequiredRole = normalizedRoles.some((role) => requiredRolesNormalized.includes(role));

                        if (!hasRequiredRole) {
                            console.warn(`RoleGuard: Roles '${normalizedRoles.join(', ')}' no tienen los permisos requeridos para esta ruta`);
                            this.router.navigate(['/landing/access']);
                            observer.next(false);
                            observer.complete();
                            return;
                        }
                    }
                    observer.next(true);
                    observer.complete();
                }).catch(error => {
                    console.error('RoleGuard: Error obteniendo rol del usuario:', error);
                    this.router.navigate(['/landing/access']);
                    observer.next(false);
                    observer.complete();
                });
            }).catch(error => {
                console.error('RoleGuard: Error validando autenticación:', error);
                this.router.navigate(['/landing/login']);
                observer.next(false);
                observer.complete();
            });
        });
    }

    canActivateChild(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        return this.canActivate(route, state);
    }
}
